/**
 * Rig chain resolution (ENG-946).
 *
 * The blueprint drives the face rig by composing POSES, not by wiring 168
 * parameters. A pose is a whole control configuration — which is what the
 * bundle's own data keeps saying: "eyes closed" is blink + lidPress + eyelidU +
 * gaze together, not one control. So the nodes traffic in configurations, and
 * you place a handful of them rather than a pin per control:
 *
 *   rigPose     a configuration (a trained pose from the bundle, or one saved
 *               into the node) — the chain's source, like playClip
 *   rigMacro    one scalar -> a whole subset, using the bundle's own macros
 *   rigControl  ONE control you actually care about, picked by name
 *   rigBlend    crossfade two chains, so ConversationalState can drive the face
 *               the same way it drives body clips
 *
 * Resolution is a CHAIN (each node transforms its input), not a weight sum:
 * a macro modifies the pose beneath it, which a scalar weight cannot express.
 * That is also why this lives outside GraphEvaluator — the face graph's normal
 * output is {clipName -> weight}, and a rig pose is a vector. GraphEvaluator
 * ignores these node types (unknown types are no-ops), so the two evaluations
 * coexist without either knowing about the other.
 */

/**
 * @typedef {object} RigChainCtx
 * @property {number}        dim       control-space width
 * @property {Float32Array}  [presets] flat trained pose table (rows * dim)
 * @property {string[]}      [names]   control names, for rigControl lookup
 * @property {object}        [macros]  { mouth?: builtMacro, eyes?: builtMacro }
 * @property {(name:string)=>any} [getVariable]
 */

/**
 * Resolve a value that may be WIRED to a data pin.
 *
 * Same precedence as the clip Blend node — wired pin > variable name > inline
 * value — so a rig node behaves the way every other node in the editor does.
 *
 * Only `getVariable` sources are resolved here. GraphEvaluator's own
 * `evaluateValue` (which also knows random/prefabSlot) is private to its
 * per-frame ctx and cache; reaching into it from this separate pass would
 * either duplicate that state or read it stale. A pin wired to anything else
 * falls through to the inline value rather than silently reading 0.
 */
function wiredValue(nodeId, handle, graph, ctx) {
  const edge = graph.edges.find(e => e.target === nodeId && e.targetHandle === handle)
  if (!edge) return null
  const src = graph.nodes.find(n => n.id === edge.source)
  if (src?.type !== 'getVariable' || !ctx?.getVariable) return null
  const v = Number(ctx.getVariable(src.data?.variableName))
  return Number.isFinite(v) ? v : null
}

/** Alpha for a node: wired pin, then variable name, then inline value. */
function alphaOf(data, ctx, nodeId, graph) {
  if (nodeId && graph) {
    const wired = wiredValue(nodeId, 'alpha-in', graph, ctx)
    if (wired !== null) return clamp01(wired)
  }
  const varName = data?.alphaVariable
  if (varName && ctx?.getVariable) {
    const v = ctx.getVariable(varName)
    if (typeof v === 'number' && Number.isFinite(v)) return clamp01(v)
  }
  const a = Number(data?.alpha)
  return Number.isFinite(a) ? clamp01(a) : 0
}

const clamp01 = (v) => Math.max(0, Math.min(1, v))

/** True when this node type participates in a rig chain. */
export function isRigChainNode(type) {
  return type === 'rigPose' || type === 'rigMacro'
    || type === 'rigControl' || type === 'rigBlend'
}

/** Does this face graph drive the rig by pose composition? */
export function hasRigChain(nodes) {
  return Array.isArray(nodes) && nodes.some(n => isRigChainNode(n?.type))
}

/**
 * Resolve the pose a rig chain produces.
 *
 * @param {string} nodeId  the node feeding Final Pose
 * @param {{nodes:any[],edges:any[]}} graph
 * @param {RigChainCtx} ctx
 * @returns {Float32Array|null} null when this branch is not a rig chain, or
 *          when the data it needs (a pose table, a macro) is not loaded — a
 *          half-resolved pose would drive the decoders with a configuration
 *          nobody authored, so we decline instead.
 */
export function resolveRigChain(nodeId, graph, ctx, _seen) {
  const seen = _seen ?? new Set()
  if (!nodeId || seen.has(nodeId)) return null // cycle guard
  seen.add(nodeId)

  const node = graph.nodes.find(n => n.id === nodeId)
  if (!node || !isRigChainNode(node.type)) return null
  const data = node.data ?? {}

  const inputFrom = (handle) => {
    const edge = graph.edges.find(e => e.target === nodeId && (handle ? e.targetHandle === handle : true))
    return edge ? resolveRigChain(edge.source, graph, ctx, seen) : null
  }

  if (node.type === 'rigPose') {
    return poseFromNode(data, ctx)
  }

  if (node.type === 'rigMacro') {
    const base = inputFrom('anim') ?? poseFromNode({ poseIndex: 0 }, ctx)
    const macro = ctx?.macros?.[data.macro]
    if (!base || !macro) return base
    const t = alphaOf(data, ctx, nodeId, graph)
    return macro.apply(base, t, macro.valueOf(base))
  }

  if (node.type === 'rigControl') {
    const base = inputFrom('anim')
    if (!base) return null
    const idx = controlIndex(data, ctx)
    if (idx < 0) return base
    const out = Float32Array.from(base)
    let v = Number(data.value)
    const wired = wiredValue(nodeId, 'value-in', graph, ctx)
    if (wired !== null) {
      v = wired
    } else if (data.valueVariable && ctx?.getVariable) {
      const fromVar = ctx.getVariable(data.valueVariable)
      if (typeof fromVar === 'number' && Number.isFinite(fromVar)) v = fromVar
    }
    if (Number.isFinite(v)) out[idx] = v
    return out
  }

  if (node.type === 'rigBlend') {
    const a = inputFrom('a')
    const b = inputFrom('b')
    if (!a) return b
    if (!b) return a
    const t = alphaOf(data, ctx, nodeId, graph)
    const out = new Float32Array(a.length)
    for (let i = 0; i < a.length; i++) out[i] = a[i] * (1 - t) + b[i] * t
    return out
  }

  return null
}

/** A rigPose node's configuration: an inline saved pose, else a trained row. */
function poseFromNode(data, ctx) {
  if (Array.isArray(data?.pose) && data.pose.length === ctx?.dim) {
    return Float32Array.from(data.pose)
  }
  const presets = ctx?.presets
  const dim = ctx?.dim
  if (!presets || !dim) return null
  const rows = Math.floor(presets.length / dim)
  if (!rows) return null
  const i = Math.max(0, Math.min(rows - 1, Number(data?.poseIndex) || 0))
  return presets.slice(i * dim, (i + 1) * dim)
}

/** rigControl addresses a control by NAME, so a reordered bundle can't silently
 *  repoint the node at a different control. */
function controlIndex(data, ctx) {
  const names = ctx?.names
  if (!names) return -1
  if (typeof data?.control === 'string' && data.control) {
    return names.indexOf(data.control)
  }
  const i = Number(data?.controlIndex)
  return Number.isInteger(i) && i >= 0 && i < names.length ? i : -1
}
